import {
  CardConditionEnum,
  CardLanguageEnum,
  FinishEnum,
} from "modules/cards/model/card";
import {
  SortOrderEnum,
  type CollectionFacetFilters,
  type FacetCountsResponse,
} from "modules/collections/details/model/collection";
import { ShortTradeList } from "modules/decks/model/deck";
import {
  type ActivityStatus,
  type GameId,
  type PricingConfiguration,
  ShortUser,
} from "modules/common/model/models";
import { EmbeddedCardPrice } from "modules/cards/model/card-price";
import { z } from "zod";

export interface TradeListItemModel {
  userId: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  id: string;
  type: TradeListType;
  itemsCount: number;
  deckId?: string;
  deckName?: string;
  published?: boolean;
  game: GameId;
  pricing: PricingConfiguration;
}

export interface TradeListDetails {
  userId: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  id: string;
  type: TradeListType;
  itemsCount: number;
  deckId?: string;
  deckName?: string;
  published?: boolean;
  game: GameId;
  pricing: PricingConfiguration;
  owner?: ShortUser;
}

export enum TradeListType {
  WANTS = 1,
  TRADES,
}

export enum TradeListItemType {
  CARDS = 1,
}

export interface TradeListCreateRequest {
  name: string;
  itemType: TradeListItemType;
  type: TradeListType;
  cardCounts?: ItemCounts;
  deckId?: string;
  published: boolean;
  game: GameId;
}

export interface TradeListCreateResponse {
  list: TradeListItemModel;
  message: "created" | "updated";
}

export interface TradeListAddItemRequest {
  listId: string;
  dataId: string;
  finishes: FinishEnum[];
  language: CardLanguageEnum;
  condition: CardConditionEnum;
  quantity: number;
  anyPrinting: boolean;
  // game: GameId;
}

export interface TradeListAddItemResponse {
  ids: string[];
}

export interface TradeListUpdateItemRequest {
  listId: string;
  ids: string[];
  finishes: FinishEnum[];
  language: CardLanguageEnum;
  condition: CardConditionEnum;
  dataId: string;
  quantity: number;
  anyPrinting: boolean;
}

export enum TradeListVisibilityEnum {
  Private,
  Public,
}

export enum TradeListSortOptions {
  CreatedAt = "createdAt",
  UpdatedAt = "updatedAt",
  Name = "name",
}

export enum TradeListItemsSortOptionsEnum {
  CreatedAt = "createdAt",
  UpdatedAt = "updatedAt",
  Name = "name",
  Price = "price",
}

export interface TradeListPagedRequest {
  userId: string;
  name: string;
  page: number;
  perPage: number;
  sort: TradeListSortOptions;
  order: SortOrderEnum;
  type?: TradeListType | null;
  wotcId?: number;
  games?: GameId[];
}

export interface TradeListPagedResponse {
  lists: TradeListItemModel[];
  hasMore: boolean;
  /** Present on page one only; continuation pages do not recount. */
  total?: number;
}

export interface TradeListItemsRequest extends CollectionFacetFilters {
  id: string;
  page: number;
  perPage: number;
  sort: ItemSortOptionsEnum;
  order: SortOrderEnum;
  query: string;
  listType?: TradeListType;
}
export interface UserTradeListItemsRequest {
  userId: string;
  page: number;
  perPage: number;
  sort: ItemSortOptionsEnum;
  order: SortOrderEnum;
  type: TradeListType;
}

export interface PagedTradeListItemsResponse {
  items: TradeItem[];
  hasMore: boolean;
  /** Returned implicitly on page one; continuation pages omit it. */
  total?: number;
}

/** Trade browse facets always return the matching grouped-row count. */
export interface TradeFacetCountsResponse extends FacetCountsResponse {
  resultTotal: number;
}

export type ActiveTradeStatus =
  | "pending"
  | "submitted"
  | "accepted"
  | "changed"
  | "completed"
  | "declined"
  | "withdrawn"
  | (string & {});

export interface ActiveTradeReference {
  chatId: string;
  quantity: number;
  partner: {
    id: string;
    username: string;
    preferredAvatar?: string;
  };
  status: ActiveTradeStatus;
  updatedAt: string;
}

export interface TradeItemAvailability {
  ids: string[];
  /** Exact concrete copies that are not reserved in a trade. */
  availableIds?: string[];
  spokenForQuantity: number;
  activityStatus?: ActivityStatus;
  activeTrades?: ActiveTradeReference[];
}

export interface TradeItemAvailabilityResponse {
  items: TradeItemAvailability[];
}

export interface ItemCounts {
  [key: string]: number;
}

export enum TradeListItemsViewEnum {
  Table,
  Visual,
}

export interface TradeItem {
  matchId?: string;
  listId?: string; // Optional, some API responses use list.id instead
  dataId: string;
  oracleId: string;
  createdAt: string;
  updatedAt: string;
  ids: string[];
  /** Live mutable subset of ids; absent until the availability overlay resolves. */
  availableIds?: string[];
  quantity: string;
  spokenForQuantity?: number;
  activityStatus?: ActivityStatus;
  activeTrades?: ActiveTradeReference[];
  list: ShortTradeList;
  finishes: FinishEnum[];
  language: CardLanguageEnum;
  condition: CardConditionEnum;
  user: ShortUser;
  anyPrinting: boolean;
  collectionCardId?: string;
  price: EmbeddedCardPrice;
  game: GameId;
  name: string;
  setCode: string;
  types: string[];
  rarity: string;
  costPrinted: string;
  costValue: number;
  number: string;
}

export enum ItemSortOptionsEnum {
  Name = "name",
  Price = "price",
  CreatedAt = "createdAt",
  UpdatedAt = "updatedAt",
  Quantity = "quantity",
}

// Re-export unified views for backward compatibility
export { UNIFIED_VIEWS as TradeItemViews } from "modules/common/model/viewTypes";

export interface DeleteTradeListResponse {
  deckId: string;
}

export interface TradeListDeleteBulkItemsRequest {
  listId: string;
  ids?: string[];
  filters?: TradeListItemsRequest;
}

export interface TradeListDeleteBulkItemsResponse {
  deleted: number;
}

export interface TradeListMoveItemsRequest {
  sourceListId: string;
  targetListId: string;
  ids?: string[];
  filters?: TradeListItemsRequest;
}

export interface TradeListMoveItemsResponse {
  moved: number;
  sourceListId: string;
  targetListId: string;
}

export interface TradeListItemMutationBatch {
  listId: string;
  ids: string[];
}

export interface TradeListMoveItemsAcrossListsRequest {
  sources: TradeListItemMutationBatch[];
  targetListId: string;
}

export interface TradeListMoveItemsAcrossListsResponse {
  moved: number;
  sourceListIds: string[];
  targetListId: string;
}

export interface TradeListDeleteItemsAcrossListsRequest {
  sources: TradeListItemMutationBatch[];
}

export interface TradeListDeleteItemsAcrossListsResponse {
  deleted: number;
  sourceListIds: string[];
}

export interface TradeItemSelectAllResponse {
  total: number;
  reserved: number;
}

export interface TradeListBulkImportRequest {
  listId: string;
  cardText: string;
  condition: CardConditionEnum;
  language: CardLanguageEnum;
  finishes: FinishEnum[];
  anyPrinting: boolean;
}

export interface TradeListBulkImportResponse {
  imported: number;
  failed: number;
  errors?: string[];
}

export interface MatchedTradesRequest {
  userId: string;
  page?: number;
  perPage?: number;
  query?: string;
}

export interface MatchedTradesResponse {
  items: TradeItem[];
  hasMore: boolean;
}

export const addCardToTradeListFormDataSchema = z.object({
  listId: z.string().min(1, "Deck ID is required"),
  dataId: z.string().min(1, "Card ID is required"),
  finishes: z.string().array().nonempty("At least one finish must be selected"),
  language: z.nativeEnum(CardLanguageEnum).default(CardLanguageEnum.English),
  condition: z
    .nativeEnum(CardConditionEnum)
    .default(CardConditionEnum.NearMint),
  quantity: z.coerce
    .number()
    .int("Quantity must be a whole number")
    .min(1, "Quantity must be at least 1"),
  anyPrinting: z.coerce.boolean(),
});
